// components/Dashboard.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Grid,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  IconButton,
  Button,
  Container,
  Paper,
  Tooltip as MuiTooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/constants';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { jwtDecode } from 'jwt-decode';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import StorefrontIcon from '@mui/icons-material/Storefront';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import SpeedIcon from '@mui/icons-material/Speed';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import PinDropIcon from '@mui/icons-material/PinDrop';
import { useNavigate } from 'react-router-dom';
// [CASH-MODE] UPI / Cash filter + split totals
import { usePermissions } from '../context/PermissionContext';
import PaymentModeFilter from '../components/PaymentModeFilter';
import PaymentModeTotals from '../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../constants/paymentModes';
import { paymentModeParam } from '../utils/paymentMode';
import { LEVEL_NAMES, levelName } from '../constants/userLevels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const LINK_PERMS = {
  '/shop/all-shops': 'merchants.allShops',
  '/withdrawal-management': 'smartPoint.withdrawals',
  '/adminControl/admins/qpay-centres': 'users.qpayCentres',
  '/adminControl/admins/qpay-agents': 'users.qpayAgents',
  '/adminControl/admins/state-heads': 'users.stateHeads',
  '/adminControl/admins/state-staff': 'users.stateStaff',
  '/customer-report/total-customers': 'customers.all',
};

// Collection (UPI / Cash) totals period filter; 'all' = no period param (legacy all-time totals)
const COLLECTION_PERIODS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
  { value: 'all', label: 'All' },
];
const collectionPeriodParam = (period) => (period && period !== 'all' ? period : undefined);
const fmtIstDay = (key) => {
  if (!key) return '';
  const [y, m, d] = String(key).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};
const collectionRangeLabel = (period, meta) => {
  if (!period || period === 'all') return 'All time';
  if (!meta || !meta.from) return '';
  if (period === 'day' || meta.from === meta.to) return `Today · ${fmtIstDay(meta.to)}`;
  return `${fmtIstDay(meta.from)} – ${fmtIstDay(meta.to)}`;
};

// Display-only renames for KPI/chart labels that still arrive from the API with old user-type names
const KPI_LABEL_RENAMES = {
  'State heads': `${LEVEL_NAMES[2]}s`,
  'State Admins': `${LEVEL_NAMES[2]}s`,
  'State Staff': `${LEVEL_NAMES[3]}s`,
  'MSB Admins': `${LEVEL_NAMES[4]}s`,
};
const kpiLabel = (text) => KPI_LABEL_RENAMES[text] || text;

// Safety net (the API already filters by the logged-in level): never show cards / chart bars about
// user types above or beside the logged-in user. Names are compared after kpiLabel renaming.
const HIDDEN_LABELS_BY_LEVEL = (() => {
  const head = kpiLabel('State heads');
  const officer = kpiLabel('State Staff');
  const centre = 'Q-Pay Centres';
  const agent = 'Q-Pay Agents';
  return {
    2: [head],
    3: [head, officer],
    4: [head, officer, centre],
    5: [head, officer, centre, agent],
  };
})();
const isHiddenForLevel = (level, text) => (HIDDEN_LABELS_BY_LEVEL[Number(level)] || []).includes(kpiLabel(text));

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [userLevel, setUserLevel] = useState(1);
  const [userName, setUserName] = useState('User');
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  // Collection totals period (Day default); toggling refetches only the totals, not the whole dashboard
  const [collectionPeriod, setCollectionPeriod] = useState('day');
  const [periodTotals, setPeriodTotals] = useState(null); // { totalsByMode, totalsPeriod } from /dashboard/collectionTotals
  const [totalsLoading, setTotalsLoading] = useState(false);
  const collectionPeriodRef = useRef('day');
  const totalsReqSeq = useRef(0);
  const navigate = useNavigate();
  const { can } = usePermissions();

  // KPI card target -> page key; a card whose page the user can't open is rendered non-clickable
  const getCardLink = (card) => {
    const isQPayCentre = card.title === 'Q-Pay Centres' || card.category === 'Q-Pay Centres';
    const target = isQPayCentre ? '/adminControl/admins/qpay-centres' : card.link;
    if (!target) return null;
    const perm = LINK_PERMS[target];
    return perm && !can(perm) ? null : target;
  };
  const canViewShops = can('merchants.allShops');
  const canManageWithdrawals = can('smartPoint.withdrawals');

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      
      let level = 1;
      let uId = '';
      if (token) {
        const decoded = jwtDecode(token);
        level = decoded.level || 1;
        uId = decoded.userId || '';
        setUserLevel(level);
      }

      if (user && user.userFirstName) {
        setUserName(`${user.userFirstName} ${user.userLastName || ''}`);
      }

      const headers = { Authorization: `Bearer ${token}` };
      const requestedPeriod = collectionPeriodRef.current;
      const response = await axios.get(`${API_BASE_URL}/dashboard/userSummary`, {
        headers,
        params: {
          level,
          userId: uId,
          paymentMode: paymentModeParam(paymentMode), // [CASH-MODE]
          period: collectionPeriodParam(requestedPeriod),
        },
      });

      setAnalytics(response.data);
      // summary already carries totals for the current period; drop the separately-fetched ones
      if (requestedPeriod === collectionPeriodRef.current) setPeriodTotals(null);
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
    } finally {
      setLoading(false);
    }
  }, [paymentMode]); // [CASH-MODE] refetch when mode changes

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleCollectionPeriodChange = async (_e, value) => {
    if (!value || value === collectionPeriodRef.current) return;
    collectionPeriodRef.current = value;
    setCollectionPeriod(value);
    const seq = ++totalsReqSeq.current;
    setTotalsLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      let level = 1;
      let uId = '';
      if (token) {
        const decoded = jwtDecode(token);
        level = decoded.level || 1;
        uId = decoded.userId || '';
      }
      const response = await axios.get(`${API_BASE_URL}/dashboard/collectionTotals`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { level, userId: uId, paymentMode: paymentModeParam(paymentMode), period: collectionPeriodParam(value) },
      });
      if (seq === totalsReqSeq.current) setPeriodTotals(response.data);
    } catch (error) {
      console.error('Error fetching collection totals:', error);
    } finally {
      if (seq === totalsReqSeq.current) setTotalsLoading(false);
    }
  };

  // Helper icon selector for summary cards
  const renderCardIcon = (iconName) => {
    switch (iconName) {
      case 'people':
        return <PeopleAltIcon />;
      case 'store':
        return <StorefrontIcon />;
      case 'wallet':
        return <AccountBalanceWalletIcon />;
      case 'money':
        return <MonetizationOnIcon />;
      case 'gift':
        return <CardGiftcardIcon />;
      default:
        return <AccountBalanceWalletIcon />;
    }
  };

  // [CASH-MODE] per-date split present?
  const hasTimelineModeSplit = !!(analytics && Array.isArray(analytics.timelineData) &&
    analytics.timelineData.some((item) => item && (item.upiAmount !== undefined || item.cashAmount !== undefined)));
  // [CASH-MODE] prefer API totalsByMode, else derive from timeline split
  const dashboardTotalsByMode = (periodTotals && periodTotals.totalsByMode)
    ? periodTotals.totalsByMode
    : (analytics && analytics.totalsByMode)
    ? analytics.totalsByMode
    : (hasTimelineModeSplit
      ? {
          UPI: { amount: analytics.timelineData.reduce((s, i) => s + (Number(i.upiAmount) || 0), 0) },
          CASH: { amount: analytics.timelineData.reduce((s, i) => s + (Number(i.cashAmount) || 0), 0) },
        }
      : null);
  // [CASH-MODE] backend may name the sum 'amount' or 'totalAmount'
  const dashboardModeAmountKey = (() => {
    const t = dashboardTotalsByMode && (dashboardTotalsByMode.UPI || dashboardTotalsByMode.CASH);
    if (!t || t.amount !== undefined) return 'amount';
    return Object.keys(t).find((k) => k !== 'count' && typeof t[k] === 'number') || 'amount';
  })();

  // Prepare line/area chart data
  const lineChartData = {
    labels: (analytics && analytics.timelineData) ? analytics.timelineData.map((item) => item.date) : [],
    datasets: [
      {
        label: 'Transaction Volume (₹)',
        data: (analytics && analytics.timelineData) ? analytics.timelineData.map((item) => item.amount) : [],
        borderColor: '#FFA500',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, 'rgba(255, 165, 0, 0.35)');
          gradient.addColorStop(1, 'rgba(255, 165, 0, 0.01)');
          return gradient;
        },
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#FFA500',
        pointBorderColor: '#ffffff',
        pointHoverBackgroundColor: '#ffffff',
        pointHoverBorderColor: '#FFA500',
        pointRadius: 4,
        pointHoverRadius: 7,
      },
      // [CASH-MODE] UPI / Cash series only when the API sends per-date split
      ...(hasTimelineModeSplit
        ? [
            {
              label: 'UPI (₹)',
              data: analytics.timelineData.map((item) => Number(item.upiAmount) || 0),
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderWidth: 2,
              tension: 0.4,
              fill: false,
              pointRadius: 3,
            },
            {
              label: 'Cash (₹)',
              data: analytics.timelineData.map((item) => Number(item.cashAmount) || 0),
              borderColor: '#10b981',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              borderWidth: 2,
              tension: 0.4,
              fill: false,
              pointRadius: 3,
            },
          ]
        : []),
    ],
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: { family: 'Plus Jakarta Sans', size: 12, weight: 'bold' },
          color: '#475569',
        },
      },
      tooltip: {
        backgroundColor: '#0f172a',
        titleFont: { family: 'Plus Jakarta Sans', size: 13, weight: 'bold' },
        bodyFont: { family: 'Plus Jakarta Sans', size: 12 },
        padding: 12,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { family: 'Plus Jakarta Sans', size: 11 }, color: '#64748b' },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(226, 232, 240, 0.6)' },
        ticks: { font: { family: 'Plus Jakarta Sans', size: 11 }, color: '#64748b' },
      },
    },
  };

  // Prepare Doughnut chart data
  const statusChartData = {
    labels: (analytics && analytics.statusData) ? analytics.statusData.map((item) => item.status) : [],
    datasets: [
      {
        data: (analytics && analytics.statusData) ? analytics.statusData.map((item) => item.count) : [],
        backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'],
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverOffset: 6,
      },
    ],
  };

  const statusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 16,
          font: { family: 'Plus Jakarta Sans', size: 11, weight: '600' },
          color: '#475569',
        },
      },
      tooltip: {
        backgroundColor: '#0f172a',
        padding: 12,
        cornerRadius: 8,
      },
    },
    cutout: '72%',
  };

  // Prepare Bar chart data
  // After renaming, 'MSB Admins' and 'Q-Pay Centres' are the same group: show it once
  const categoryRows = (analytics && analytics.categoryDistribution)
    ? analytics.categoryDistribution.filter((item) => !isHiddenForLevel(userLevel, item.category)).reduce((rows, item) => {
        const label = kpiLabel(item.category);
        if (!rows.some((r) => r.label === label)) rows.push({ label, count: item.count });
        return rows;
      }, [])
    : [];
  const categoryChartData = {
    labels: categoryRows.map((r) => r.label),
    datasets: [
      {
        label: 'Platform Entity Counts',
        data: categoryRows.map((r) => r.count),
        backgroundColor: [
          'rgba(255, 165, 0, 0.85)',
          'rgba(59, 130, 246, 0.85)',
          'rgba(16, 185, 129, 0.85)',
          'rgba(139, 92, 246, 0.85)',
          'rgba(239, 68, 68, 0.85)',
        ],
        borderRadius: 8,
      },
    ],
  };

  const categoryChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { backgroundColor: '#0f172a', padding: 12, cornerRadius: 8 },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { family: 'Plus Jakarta Sans', size: 11 } } },
      y: { beginAtZero: true, grid: { color: 'rgba(226, 232, 240, 0.6)' } },
    },
  };

  if (!loading && analytics && analytics.noPincodesAssigned) {
    return (
      <Container maxWidth="md" sx={{ mt: 6, mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 4,
            border: '1px solid #fef3c7',
            backgroundColor: '#fffbeb',
            boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
          }}
        >
          <LocationOffIcon sx={{ fontSize: 60, color: '#f59e0b', mb: 2 }} />
          <Typography variant="h5" sx={{ color: '#b45309', fontWeight: 800 }} gutterBottom>
            No Pincodes Assigned
          </Typography>
          <Typography variant="body1" sx={{ color: '#92400e', maxWidth: 600, mx: 'auto', mb: 3, lineHeight: 1.6 }}>
            You currently have no area pincodes assigned to your account. According to security rules, dashboard metrics and shop analytics are strictly generated based on your assigned pincodes. Please contact your Super Admin to assign pincodes to your profile.
          </Typography>
          <Chip label={`User Level: Level ${userLevel}`} color="warning" sx={{ fontWeight: 700, borderRadius: 2 }} />
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 1, mb: 4, px: { xs: 1, sm: 2 } }}>
      {/* Top Welcome Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 3,
          borderRadius: 3.5,
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                Welcome back, {userName}!
              </Typography>
              <Chip
                label={(analytics && analytics.roleTitle) ? analytics.roleTitle : levelName(userLevel)}
                color="primary"
                sx={{ fontWeight: 700, borderRadius: 2, fontSize: '0.8125rem' }}
              />
              {(analytics && analytics.assignedPincodes && analytics.assignedPincodes.length > 0) && (
                <Chip
                  icon={<PinDropIcon style={{ color: '#0284c7', fontSize: 16 }} />}
                  label={`Assigned Pincodes (${analytics.assignedPincodes.length}): ${analytics.assignedPincodes.join(', ')}`}
                  variant="outlined"
                  sx={{ fontWeight: 700, borderColor: '#38bdf8', color: '#0369a1', bgcolor: '#f0f9ff', borderRadius: 2 }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Real-time infographics, transaction volume curves, and entity distribution analytics
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* [CASH-MODE] */}
            <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} />
            <MuiTooltip title="Refresh Dashboard Data">
              <IconButton
                onClick={fetchDashboardData}
                color="primary"
                sx={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 2.5,
                  p: 1,
                  backgroundColor: '#ffffff',
                  '&:hover': { backgroundColor: 'rgba(255, 165, 0, 0.08)' },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </MuiTooltip>
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={50} thickness={4} sx={{ color: '#FFA500' }} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* KPI Infographic Cards */}
          <Grid container spacing={2.5}>
            {(analytics && analytics.summaryCards) ? analytics.summaryCards.filter((card) => !isHiddenForLevel(userLevel, card.title)).map((card, index) => {
              const cardLink = getCardLink(card);
              return (
              <Grid item xs={12} sm={6} md={4} lg={userLevel === 1 ? 2 : 3} key={index}>
                <Card
                  elevation={0}
                  onClick={() => {
                    if (cardLink) {
                      navigate(cardLink);
                    }
                  }}
                  sx={{
                    height: '100%',
                    borderRadius: 3.5,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.04)',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.25s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: cardLink ? 'pointer' : 'default',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
                      borderColor: card.color,
                    },
                  }}
                >
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: card.color }} />
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.8125rem' }}>
                          {kpiLabel(card.title)}
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 1, fontWeight: 800, color: '#0f172a' }}>
                          {card.value}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                          <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981' }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981' }}>
                            {kpiLabel(card.growth)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: `${card.color}15`,
                          borderRadius: '12px',
                          width: 46,
                          height: 46,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: card.color,
                          flexShrink: 0,
                          '& svg': { fontSize: 24 },
                        }}
                      >
                        {renderCardIcon(card.icon)}
                      </Box>
                    </Box>
                    {cardLink && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 1.5, gap: 0.5 }}>
                        <Typography variant="caption" sx={{ color: card.color, fontWeight: 700, fontSize: '0.75rem' }}>
                          View Details
                        </Typography>
                        <ArrowForwardIcon sx={{ fontSize: 14, color: card.color }} />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              );
            }) : null}
          </Grid>

          {/* [CASH-MODE] UPI vs Cash totals */}
          {dashboardTotalsByMode && (
            <Card elevation={0} sx={{ p: 2.5, borderRadius: 3.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Collection
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 20 }}>
                    <Typography variant="caption" color="text.secondary">
                      {collectionRangeLabel(collectionPeriod, (periodTotals && periodTotals.totalsPeriod) || (analytics && analytics.totalsPeriod))}
                    </Typography>
                    {totalsLoading && <CircularProgress size={12} thickness={5} sx={{ color: '#FFA500' }} />}
                  </Box>
                </Box>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={collectionPeriod}
                  onChange={handleCollectionPeriodChange}
                  aria-label="Collection period"
                  sx={{
                    backgroundColor: '#f8fafc',
                    borderRadius: 2,
                    '& .MuiToggleButton-root': {
                      px: 1.75,
                      py: 0.5,
                      border: '1px solid #e2e8f0',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                    },
                    '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
                      backgroundColor: '#FFA500',
                      borderColor: '#FFA500',
                      color: '#ffffff',
                    },
                  }}
                >
                  {COLLECTION_PERIODS.map((p) => (
                    <ToggleButton key={p.value} value={p.value} aria-label={p.label}>
                      {p.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ opacity: totalsLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                <PaymentModeTotals totals={dashboardTotalsByMode} amountKey={dashboardModeAmountKey} title="Collection" sx={{ my: 0 }} />
              </Box>
            </Card>
          )}

          {/* Primary Infographics Section (Line & Donut Charts) */}
          <Grid container spacing={2.5}>
            {/* Timeline Line Chart */}
            <Grid item xs={12} lg={8}>
              <Card elevation={0} sx={{ p: 3, borderRadius: 3.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff', height: 380 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      Transaction Volume & Trends (₹)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Daily processed transactions and payout flow curve
                    </Typography>
                  </Box>
                  <Chip label="Live Analytics" size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
                </Box>
                <Box sx={{ height: 290, width: '100%' }}>
                  <Line data={lineChartData} options={lineChartOptions} />
                </Box>
              </Card>
            </Grid>

            {/* Status Donut Chart */}
            <Grid item xs={12} lg={4}>
              <Card elevation={0} sx={{ p: 3, borderRadius: 3.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff', height: 380 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Gateway Status Breakdown
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Transaction resolution & status percentage
                  </Typography>
                </Box>
                <Box sx={{ height: 270, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Doughnut data={statusChartData} options={statusChartOptions} />
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Secondary Infographics Section (Bar Chart & Performance Gauges) */}
          <Grid container spacing={2.5}>
            {/* Category Bar Chart */}
            <Grid item xs={12} lg={6}>
              <Card elevation={0} sx={{ p: 3, borderRadius: 3.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff', height: 360 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Entity & Category Distribution
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Platform merchant, digital VPA, and partner proportions
                  </Typography>
                </Box>
                <Box sx={{ height: 260, width: '100%' }}>
                  <Bar data={categoryChartData} options={categoryChartOptions} />
                </Box>
              </Card>
            </Grid>

            {/* System Health & Conversion Gauges */}
            <Grid item xs={12} lg={6}>
              <Card elevation={0} sx={{ p: 3, borderRadius: 3.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff', height: 360 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <SpeedIcon sx={{ color: 'primary.main', fontSize: 26 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      System Health & Performance Ratings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Automated operational quality and conversion scores
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 3 }}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                        Settlement Success Rate
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#10b981' }}>
                        {(analytics && analytics.performanceMetrics && analytics.performanceMetrics.settlementSuccessRate) ? analytics.performanceMetrics.settlementSuccessRate : 98.4}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(analytics && analytics.performanceMetrics && analytics.performanceMetrics.settlementSuccessRate) ? analytics.performanceMetrics.settlementSuccessRate : 98.4}
                      sx={{ height: 10, borderRadius: 5, backgroundColor: '#f1f5f9', '& .MuiLinearProgress-bar': { backgroundColor: '#10b981' } }}
                    />
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                        Merchant Active Retention Rate
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#FFA500' }}>
                        {(analytics && analytics.performanceMetrics && analytics.performanceMetrics.activeMerchantRate) ? analytics.performanceMetrics.activeMerchantRate : 91.2}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(analytics && analytics.performanceMetrics && analytics.performanceMetrics.activeMerchantRate) ? analytics.performanceMetrics.activeMerchantRate : 91.2}
                      sx={{ height: 10, borderRadius: 5, backgroundColor: '#f1f5f9', '& .MuiLinearProgress-bar': { backgroundColor: '#FFA500' } }}
                    />
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                        KYC & Document Verification Rate
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                        {(analytics && analytics.performanceMetrics && analytics.performanceMetrics.kycVerificationRate) ? analytics.performanceMetrics.kycVerificationRate : 94.6}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(analytics && analytics.performanceMetrics && analytics.performanceMetrics.kycVerificationRate) ? analytics.performanceMetrics.kycVerificationRate : 94.6}
                      sx={{ height: 10, borderRadius: 5, backgroundColor: '#f1f5f9', '& .MuiLinearProgress-bar': { backgroundColor: '#3b82f6' } }}
                    />
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Action Navigation Bar */}
          {(canViewShops || canManageWithdrawals) && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3.5,
              border: '1px solid #e2e8f0',
              backgroundColor: '#ffffff',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <VerifiedUserIcon sx={{ color: 'primary.main', fontSize: 24 }} />
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                Quick Platform Management
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {canViewShops && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/shop/all-shops')}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                View Registered Shops
              </Button>
              )}
              {canManageWithdrawals && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/withdrawal-management')}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Manage Withdrawals
              </Button>
              )}
            </Box>
          </Paper>
          )}
        </Box>
      )}
    </Container>
  );
};

export default Dashboard;